import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { StatusBadge } from "../components/StatusBadge";
import { EmptyState } from "../components/EmptyState";
import { MessageThreadDialog } from "../components/MessageThreadDialog";
import { PaginationBar, usePagination } from "../components/Pagination";
import { useIsMobile } from "../hooks/useIsMobile";
import { useT } from "../i18n/LanguageProvider";
import { listMyVerifications } from "../api/verifications";
import { createDownloadUrl } from "../api/storage";
import { useAuth } from "../hooks/useAuth";
import { C, font } from "../styles/tokens";
import { InlineIcon } from "../components/InlineIcon";

const TABS = ["ALL", "PENDING", "VERIFIED", "REJECTED"];

export function MyVerificationsPage() {
  const t = useT();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, localUser } = useAuth();
  const currentUser = user || localUser;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && TABS.includes(tabParam.toUpperCase())) {
      return tabParam.toUpperCase();
    }
    return "ALL";
  });
  const [threadItem, setThreadItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && TABS.includes(tabParam.toUpperCase())) {
      setTab(tabParam.toUpperCase());
    }
  }, [searchParams]);

  useEffect(() => {
    if (items.length === 0) return;
    const reqId = searchParams.get("request");
    if (reqId) {
      const match = items.find((it) => it.id === reqId);
      if (match) setSelectedItem(match);
    }
    const threadId = searchParams.get("thread");
    if (threadId) {
      const match = items.find((it) => it.id === threadId);
      if (match) setThreadItem(match);
    }
  }, [items, searchParams]);

  const showSuccess = searchParams.get("success") === "1";
  const showEdit = searchParams.get("editRequested") === "1";

  async function handleOpenFile(item) {
    const key = item?.credential?.certificateS3Key;
    if (!key) return;
    try {
      const res = await createDownloadUrl(key);
      const url = res?.downloadUrl || res?.url || res;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      alert(
        "Failed to open proof document: " + (err.message || "Unknown error"),
      );
    }
  }

  useEffect(() => {
    listMyVerifications()
      .then(setItems)
      .catch((err) => setError(err.message ?? t("common.somethingWrong")))
      .finally(() => setLoading(false));
  }, []);

  function dismissBanner() {
    const next = new URLSearchParams(searchParams);
    next.delete("success");
    next.delete("editRequested");
    setSearchParams(next, { replace: true });
  }

  const filtered = items.filter((i) => tab === "ALL" || i.status === tab);
  const pager = usePagination(filtered, 10);
  const counts = {
    ALL: items.length,
    PENDING: items.filter((i) => i.status === "PENDING").length,
    VERIFIED: items.filter((i) => i.status === "VERIFIED").length,
    REJECTED: items.filter((i) => i.status === "REJECTED").length,
  };

  return (
    <AppShell>
      {/* Stack title + CTA on mobile so the "+ Request new" button doesn't
          get squeezed off-screen by the long heading + subtitle. */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 700,
              margin: "0 0 4px",
              letterSpacing: -0.4,
            }}
          >
            {t("verify.myVerifications")}
          </h1>
          <p style={{ fontSize: 13, color: C.textMed, margin: 0 }}>
            Track the status of all your verification requests.
          </p>
        </div>
        <button
          onClick={() => navigate("/verify/request")}
          style={{
            padding: "10px 18px",
            fontSize: 13,
            fontWeight: 600,
            color: "white",
            background: C.blue,
            border: 0,
            borderRadius: 4,
            cursor: "pointer",
            fontFamily: font,
            whiteSpace: "nowrap",
            alignSelf: isMobile ? "stretch" : "auto",
          }}
        >
          + {t("verify.requestNew").replace("+ ", "")}
        </button>
      </div>

      {/* Success / edit banners */}
      {(showSuccess || showEdit) && (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: 16,
            borderRadius: 6,
            background: C.greenLight,
            border: `1px solid ${C.green}33`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 13, color: C.green, fontWeight: 600 }}>
            {showEdit
              ? "Edit request submitted. The verifier will review your proposed changes."
              : "Payment received. Your request is now in the verifier's queue."}
          </div>
          <button
            onClick={dismissBanner}
            aria-label="Dismiss"
            style={{
              background: "transparent",
              border: 0,
              color: C.textMed,
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
              padding: 0,
            }}
          >
            <InlineIcon name="close" size={18} />
          </button>
        </div>
      )}

      {error && (
        <div
          style={{ ...cardStyle, color: C.red, marginBottom: 16, fontSize: 13 }}
        >
          {error}
        </div>
      )}

      <div style={{ ...cardStyle, padding: isMobile ? 16 : 24 }}>
        {/* Tabs -- horizontally scrollable on mobile so they never overflow */}
        <div
          style={{
            borderBottom: `1px solid ${C.divider}`,
            marginBottom: 16,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div style={{ display: "flex", gap: 2, minWidth: "fit-content" }}>
            {TABS.map((key) => (
              <button
                key={key}
                onClick={() => {
                  setTab(key);
                  pager.reset();
                }}
                style={{
                  padding: "8px 14px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: tab === key ? C.blue : C.textMed,
                  background: "transparent",
                  border: 0,
                  borderBottom:
                    tab === key
                      ? `2px solid ${C.blue}`
                      : "2px solid transparent",
                  marginBottom: -1,
                  cursor: "pointer",
                  fontFamily: font,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {key.charAt(0) + key.slice(1).toLowerCase()}
                <span
                  style={{
                    padding: "1px 6px",
                    fontSize: 10,
                    fontWeight: 700,
                    color: tab === key ? C.blueDark : C.textLight,
                    background: tab === key ? C.blueLight : C.divider,
                    borderRadius: 8,
                  }}
                >
                  {counts[key]}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ padding: "20px 0", fontSize: 13, color: C.textMed }}>
            {t("common.loading")}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            kind={tab === "ALL" ? "shield" : "check"}
            title={
              tab === "ALL"
                ? "No verification requests yet"
                : `No ${tab.toLowerCase()} requests`
            }
            body={
              tab === "ALL"
                ? "Request verification from the institution or organisation that issued your credential."
                : undefined
            }
            ctaLabel={
              tab === "ALL" ? "Request your first verification" : undefined
            }
            onCtaClick={
              tab === "ALL" ? () => navigate("/verify/request") : undefined
            }
            size="medium"
          />
        ) : (
          <>
            {pager.pageItems.map((item, i) => (
              <VerificationRow
                key={item.id}
                item={item}
                showDivider={i > 0}
                onSelect={setSelectedItem}
                onOpenThread={() => setThreadItem(item)}
              />
            ))}
            <PaginationBar
              pager={pager}
              itemLabel="requests"
              style={{
                margin: isMobile ? "12px -16px -16px" : "12px -24px -24px",
                borderRadius: "0 0 8px 8px",
              }}
            />
          </>
        )}
      </div>

      {threadItem && (
        <MessageThreadDialog
          requestId={threadItem.id}
          title={`${threadItem.credential?.title || "Verification"} - ${threadItem.institution?.name ?? threadItem.organisation?.name ?? ""}`}
          onClose={() => setThreadItem(null)}
        />
      )}

      {selectedItem && (
        <VerificationDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onOpenFile={handleOpenFile}
          onOpenThread={(item) => {
            setSelectedItem(null);
            setThreadItem(item);
          }}
          user={currentUser}
        />
      )}
    </AppShell>
  );
}

function VerificationRow({ item, showDivider, onOpenThread, onSelect }) {
  const [hover, setHover] = useState(false);
  const isMobile = useIsMobile();
  const cred = item.credential ?? {};
  const verifierName =
    item.institution?.name ?? item.organisation?.name ?? "Unknown verifier";
  const verifierCity = item.institution?.city ?? item.organisation?.city ?? "";
  const isEdit = item.context === "EDIT";

  return (
    <div
      onClick={() => onSelect?.(item)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.(item);
        }
      }}
      style={{
        padding: "14px 12px",
        margin: "0 -12px",
        borderRadius: 8,
        background: hover ? "#f8fafc" : "transparent",
        borderTop: showDivider ? `1px solid ${C.divider}` : "none",
        cursor: "pointer",
        transition: "background 0.15s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 4,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 15, fontWeight: 600, color: C.textDark }}>
              {cred.title || "Verification Request"}
            </span>
            <StatusBadge status={item.status} />
            {isEdit && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.amber,
                  background: C.amberLight,
                  padding: "1px 7px",
                  borderRadius: 8,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                Edit
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: C.textMed }}>
            {cred.issuer ? `${cred.issuer} - ` : ""}
            {verifierName}
            {verifierCity && `, ${verifierCity}`}
          </div>
          <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
            Requested {formatDate(item.createdAt)}
            {item.decidedAt && (
              <> - Decided {formatDate(item.decidedAt)}</>
            )} -{" "}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpenThread();
              }}
              style={{
                background: isMobile ? "#eff6ff" : "transparent",
                border: isMobile ? "1px solid #bfdbfe" : 0,
                borderRadius: isMobile ? 4 : 0,
                padding: isMobile ? "3px 8px" : 0,
                fontSize: 11,
                fontWeight: 600,
                color: C.blue,
                cursor: "pointer",
                fontFamily: font,
                display: "inline-flex",
                alignItems: "center",
                minHeight: isMobile ? 26 : undefined,
              }}
              title="Message the verifier about this request"
            >
              Messages
            </button>
          </div>
          {item.status === "REJECTED" && item.decisionReason && (
            <div
              style={{
                marginTop: 8,
                padding: "8px 10px",
                background: C.redLight,
                borderRadius: 4,
                fontSize: 12,
                color: C.red,
                lineHeight: 1.4,
              }}
            >
              <strong>Reason:</strong> {item.decisionReason}
            </div>
          )}
        </div>

        <span style={{ fontSize: 16, color: "#cbd5e1", flexShrink: 0 }}>
          <InlineIcon name="chevronRight" size={16} />
        </span>
      </div>
    </div>
  );
}

function VerificationDetailModal({
  item,
  onClose,
  onOpenFile,
  onOpenThread,
  user,
}) {
  const cred = item.credential ?? {};
  const verifierName =
    item.institution?.name ?? item.organisation?.name ?? "Verifier";
  const verifierCity = item.institution?.city ?? item.organisation?.city ?? "";
  const fullName =
    user?.fullName ||
    `${user?.professional?.firstName ?? ""} ${user?.professional?.lastName ?? ""}`.trim() ||
    "Verified Citizen";
  const email = user?.email || "";
  const hasCert = !!cred.certificateS3Key;
  const metaPairs = metadataPairs(cred.metadata);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(15, 23, 42, 0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 620,
          background: "#ffffff",
          borderRadius: 16,
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
          fontFamily: font,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "#eff6ff",
                color: "#2563eb",
                fontWeight: 700,
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {getInitials(fullName)}
            </div>
            <div>
              <h3
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: "#0f172a",
                  margin: 0,
                }}
              >
                {fullName}
              </h3>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                {email}
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              color: "#94a3b8",
              cursor: "pointer",
              padding: 4,
            }}
          >
            <InlineIcon name="close" size={18} />
          </button>
        </div>

        {/* Modal Content */}
        <div
          style={{
            padding: 24,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              background: "#f8fafc",
              borderRadius: 12,
              padding: 16,
              border: "1px solid #f1f5f9",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 4,
              }}
            >
              Credential Details
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
              {cred.title || "Academic Document"}
            </div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>
              {[
                cred.issuer,
                labelType(cred.type),
                formatDateRange(cred.startDate, cred.endDate, cred.isCurrent),
              ]
                .filter(Boolean)
                .join(" | ")}
            </div>

            {(cred.startDate ||
              cred.endDate ||
              cred.isCurrent ||
              metaPairs.length > 0 ||
              verifierName) && (
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: "1px solid #e2e8f0",
                  fontSize: 12,
                  color: "#334155",
                }}
              >
                {verifierName && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      margin: "4px 0",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Verifier:</span>
                    <span style={{ fontWeight: 600 }}>
                      {verifierName}
                      {verifierCity ? `, ${verifierCity}` : ""}
                    </span>
                  </div>
                )}
                {cred.startDate && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      margin: "4px 0",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>From Date:</span>
                    <span style={{ fontWeight: 600 }}>
                      {formatMonthYear(cred.startDate)}
                    </span>
                  </div>
                )}
                {(cred.endDate || cred.isCurrent) && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      margin: "4px 0",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>End Date:</span>
                    <span style={{ fontWeight: 600 }}>
                      {cred.isCurrent
                        ? "Present"
                        : formatMonthYear(cred.endDate)}
                    </span>
                  </div>
                )}
                {metaPairs.map(([k, v]) => (
                  <div
                    key={k}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      margin: "4px 0",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>{humanizeKey(k)}:</span>
                    <span style={{ fontWeight: 600 }}>
                      {formatMetaValue(k, v)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {cred.description && (
            <div
              style={{
                fontSize: 13,
                color: "#475569",
                background: "#f8fafc",
                padding: 12,
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                Description
              </div>
              <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {cred.description}
              </p>
            </div>
          )}

          {item.context === "EDIT" && item.proposedChanges && (
            <div
              style={{
                background: "#fffbeb",
                borderRadius: 12,
                padding: 16,
                border: "1px solid #fef3c7",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#b45309",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 6,
                }}
              >
                Proposed Changes (Under Review)
              </div>
              <div style={{ fontSize: 12, color: "#78350f" }}>
                {Object.entries(item.proposedChanges)
                  .filter(([k, v]) => v !== null && v !== undefined && v !== "")
                  .map(([k, v]) => (
                    <div
                      key={k}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        margin: "4px 0",
                      }}
                    >
                      <span style={{ color: "#92400e" }}>
                        {humanizeKey(k)}:
                      </span>
                      <span style={{ fontWeight: 600 }}>
                        {typeof v === "object" ? JSON.stringify(v) : String(v)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {item.requestNote && (
            <div
              style={{
                fontSize: 13,
                color: "#334155",
                background: "#eff6ff",
                padding: 12,
                borderRadius: 8,
              }}
            >
              <strong style={{ color: "#2563eb" }}>Note submitted: </strong>
              {item.requestNote}
            </div>
          )}

          {hasCert && (
            <button
              onClick={() => onOpenFile(item)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "10px 16px",
                borderRadius: 8,
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                color: "#15803d",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              View Uploaded Certificate / Proof File
              <InlineIcon name="external" size={14} />
            </button>
          )}

          {item.status === "REJECTED" && item.decisionReason && (
            <div
              style={{
                fontSize: 13,
                color: "#dc2626",
                background: "#fef2f2",
                padding: 12,
                borderRadius: 8,
              }}
            >
              <strong>Rejection reason: </strong>
              {item.decisionReason}
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div
          style={{
            padding: "16px 24px",
            background: "#f8fafc",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={() => {
              onClose();
              onOpenThread(item);
            }}
            style={{
              padding: "8px 14px",
              fontSize: 13,
              fontWeight: 600,
              color: "#2563eb",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Message Verifier
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color:
                  item.status === "VERIFIED"
                    ? "#16a34a"
                    : item.status === "REJECTED"
                      ? "#dc2626"
                      : "#d97706",
              }}
            >
              Status: {item.status}
            </span>
            <button
              onClick={onClose}
              style={{
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                color: "#475569",
                background: "#ffffff",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatMonthYear(dateVal) {
  if (!dateVal) return "";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return String(dateVal);
  return d.toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatDateRange(start, end, isCurrent) {
  const s = formatMonthYear(start);
  const e = isCurrent ? "Present" : formatMonthYear(end);
  if (s && e) return `${s} - ${e}`;
  return s || e || "";
}

function parseMetadata(meta) {
  if (!meta) return {};
  if (typeof meta === "string") {
    try {
      return JSON.parse(meta);
    } catch {
      return {};
    }
  }
  return typeof meta === "object" ? meta : {};
}

function metadataPairs(metadata) {
  const metaObj = parseMetadata(metadata);
  const excluded = new Set([
    "startDate",
    "endDate",
    "fromDate",
    "toDate",
    "isCurrent",
    "branchId",
    "branchOrganisationId",
  ]);
  return Object.entries(metaObj).filter(
    ([k, v]) => !excluded.has(k) && v !== null && v !== "" && v !== undefined,
  );
}

function formatMetaValue(key, value) {
  if (key === "ctc" && !isNaN(Number(value))) {
    return `₹${Number(value).toLocaleString("en-IN")}`;
  }
  return String(value);
}

function humanizeKey(key) {
  if (key === "ctc") return "Annual CTC";
  if (key === "gpa") return "GPA";
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/Id$/, "ID");
}

function labelType(type) {
  return (
    { EDUCATION: "Education", WORK: "Work experience", OTHER: "Other" }[type] ??
    type
  );
}

function getInitials(name) {
  if (!name) return "AP";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

const cardStyle = {
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 8,
  padding: 24,
};
